import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatChipInputEvent } from "@angular/material/chips";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Sort } from "@angular/material/sort";
import { ActivatedRoute } from "@angular/router";
import { BehaviorSubject, Subject } from "rxjs";
import { debounceTime, filter, flatMap, map, mergeMap, takeUntil, tap } from "rxjs/operators";
import { ConfirmationDialogComponent } from "../general/confirmationDialog.component";
import { StreamService, TaxonomyService } from "../services";
import { TenantService } from "../services/tenant.service";
import * as _ from 'lodash';
import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";

@Component({
    styles: [`
        .streamsPanel {
            overflow-y: auto;
            height: calc(100vh - 137px);
        }
        .cdk-column-title.mat-column-title {
            width: calc(100vw - 800px);
            min-width: calc(100vw - 800px);
        }
        .cdk-column-type.mat-column-type {
            width: 50px;
            min-width: 50px;
            text-align: center;
        }
        .cdk-column-itemsCount.mat-column-itemsCount {
            width: 85px;
            min-width: 85px;
            text-align: center;
        }
        .cdk-column-followersCount.mat-column-followersCount {
            width: 85px;
            min-width: 85px;
            text-align: center;
        }
        .cdk-column-aggregationStatus.mat-column-aggregationStatus {
            text-align: center;
            width: 55px;
            min-width: 55px;
        }
        .cdk-column-active.mat-column-active {
            width: 55px;
            min-width: 55px;
            text-align: center;
        }
        .cdk-column-actions.mat-column-actions {
            width: 65px;
            min-width: 65px;
        }
    `],
    template: `
        <mat-toolbar>
            <mat-form-field style="font-size: 14px;">
                <input matInput placeholder="Search" [formControl]="searchTermControl">
            </mat-form-field>
            <mat-form-field fxFlex="auto" style="font-size: 14px; font-weight: 400; padding-left: 20px">
                    <mat-chip-list #chipList>
                        <mat-chip *ngFor="let tag of searchTags | async" color="primary" 
                                  (removed)="removeTag(tag)"
                                  class="small-chip">
                            {{tag.label}}
                            <mat-icon matChipRemove>cancel</mat-icon>
                        </mat-chip>
                        <input placeholder="Search Tags"
                               #tagInputCtrl
                               [formControl]="tagCtrl"
                               [matAutocomplete]="auto"
                               [matChipInputFor]="chipList"
                               (matChipInputTokenEnd)="add($event)">
                    </mat-chip-list>
                    <mat-autocomplete #auto="matAutocomplete" (optionSelected)="tagSelected($event)" [autoActiveFirstOption]="true">
                        <mat-option *ngFor="let tag of filteredTags" [value]="tag">{{tag.label}}</mat-option>
                    </mat-autocomplete>
                </mat-form-field>
                <mat-select placeholder="Category" [formControl]="categoryControl" style="max-width: 110px; padding-right: 15px; padding-left: 15px;  font-size: 14px;">
                    <mat-option></mat-option>
                    <mat-option *ngFor="let category of categories" [value]="category.id">{{category.title}}</mat-option>
                </mat-select>
        </mat-toolbar>
        <div *ngIf="noTouching" fxFlex fxLayoutAlign="center center"><spinner></spinner></div>
        <div *ngIf="!emptySearch"
                class="streamsPanel"
                infiniteScroll 
                [infiniteScrollThrottle]="300"
                [infiniteScrollDistance]="1"
                [scrollWindow]="false"
                (scrolled)="nextPage()">
            <table mat-table
                matSort 
                (matSortChange)="sort($event)"
                [matSortDisableClear]="true "
                [matSortActive]="params?.value?.sort" 
                [matSortDirection]="params?.value?.sortDirection" 
                *ngIf="!noTouching" 
                [dataSource]="streams" 
                style="width: 100%;">
                <ng-container matColumnDef="title">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="titleLower">Title</th>
                    <td mat-cell *matCellDef="let row">{{row.title}}</td>
                </ng-container>
                <ng-container matColumnDef="type">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="type" style="padding-left: 15px;">Type</th>
                    <td mat-cell *matCellDef="let row">
                        <mat-icon [fontSet]="types[row.type].set" 
                                    [fontIcon]="types[row.type].icon"
                                    [matTooltip]="types[row.type].label"></mat-icon>
                    </td>
                </ng-container>
                <ng-container matColumnDef="itemsCount">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="itemsCount" start="desc" style="padding-left: 33px;">Items</th>
                    <td mat-cell *matCellDef="let row">{{row.itemsCount}}</td>
                </ng-container>
                <ng-container matColumnDef="followersCount">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="followersCount" start="desc" style="padding-left: 33px;">Followers</th>
                    <td mat-cell *matCellDef="let row">{{row.followersCount}}</td>
                </ng-container>
                <ng-container matColumnDef="created">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="created" start="desc">Created</th>
                    <td mat-cell *matCellDef="let row">{{row.created | date:'shortDate'}}</td>
                </ng-container>
                <ng-container matColumnDef="actions">
                    <th mat-header-cell *matHeaderCellDef></th>
                    <td mat-cell *matCellDef="let row">
                        <mat-menu #menu="matMenu">
                            <a *ngIf="row.type !== 'COLLECTION'" mat-menu-item [routerLink]="['./sources', row.id]">
                                <mat-icon>build</mat-icon>
                                <span>Edit</span>
                            </a>
                            <a *ngIf="row.type === 'COLLECTION'" mat-menu-item [routerLink]="['./topics', row.id]">
                                <mat-icon>build</mat-icon>
                                <span>Edit</span>
                            </a>
                            <button mat-menu-item (click)="delete(row)"><mat-icon>clear</mat-icon>Remove</button>
                        </mat-menu>
                        <button mat-icon-button class="edit-button" mat-icon-button [matMenuTriggerFor]="menu">
                            <mat-icon>settings</mat-icon>
                        </button>    
                    </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="['title', 'type', 'itemsCount', 'followersCount', 'created', 'actions']"></tr>
                <tr mat-row *matRowDef="let row; columns: ['title', 'type', 'itemsCount', 'followersCount','created', 'actions'];"></tr>
            </table>
            <div *ngIf="noTouchingPaging" fxLayoutAlign="center center">
                <spinner></spinner>
            </div>
        </div>
        <div *ngIf="emptySearch" fxLayoutAlign="center center">
            <h3>No sources match your search criteria. <button mat-button (click)="clearSearch()">Clear search.</button></h3>
        </div>
    `
})
export class CatalogEditStreamsComponent implements OnInit, OnDestroy {
    noTouching: boolean;
    noTouchingPaging: boolean;
    lastFetched: boolean;
    emptySearch: boolean;
    searchTermControl = new FormControl();
    params = new BehaviorSubject<any>({ sort: 'titleLower', sortDirection: 'asc', page: 0,  rows: 40 });
    streams = new BehaviorSubject<any[]>([]);
    private cleanupTime = new Subject<void>();
    types = StreamService.types;
    @ViewChild('tagInputCtrl') tagInput: ElementRef<HTMLInputElement>;
    streamTags: any;
    searchTags = new BehaviorSubject<any[]>(null);
    tagCtrl = new FormControl();
    filteredTags = [];
    categories = [];
    categoryControl = new FormControl();
    categoryId: any;

    constructor(private route: ActivatedRoute,
                private snackBar: MatSnackBar,
                private dialog: MatDialog,
                private taxonomyService: TaxonomyService,
                private tenantService: TenantService) {}

    ngOnInit(): void {
        this.route.parent.parent.params.pipe(
            filter(params => !!params.catalogId),
            map(params => ({ catalogId: params.catalogId })),
            takeUntil(this.cleanupTime)
        ).subscribe(updated => this.fetch(Object.assign({}, this.params.value, updated)));
        
        this.tenantService.getCatalogTags()
        .pipe(
            tap(tags => this.streamTags = tags),
            filter(() => this.route.snapshot.queryParams.tag),
            map(() => this.streamTags.find(tag => tag.id === this.route.snapshot.queryParams.tag)),
            filter(tag => !!tag),
            map(tag => (this.searchTags.value || []).concat([tag]))
        )
        .subscribe(updated => this.searchTags.next(updated));

        this.tagCtrl.valueChanges.pipe(takeUntil(this.cleanupTime))
        .subscribe(() => {
            const val = this.tagCtrl.value?.toLowerCase ? this.tagCtrl.value.toLowerCase() : '';
            this.filteredTags = _.filter(this.availableTags(), tag => tag.label.toLowerCase().includes(val));
        })

        this.searchTags
        .pipe(
            filter(tags => !!tags), 
            map(tags => ({ tagIds: tags.length ? tags.map(tag => tag.id).join(',') : undefined })),
            takeUntil(this.cleanupTime )
        ).subscribe(updates => this.fetch(Object.assign({}, this.params.value, updates)));

        this.params
        .pipe(
            filter(params => !!params?.catalogId),
            flatMap(params => this.tenantService.getCatalogStreams(params.catalogId, params)),
            tap(() => this.noTouchingPaging = false),
            tap(streams => this.lastFetched = streams.last),
            takeUntil(this.cleanupTime)
        ).subscribe(streams => this.streams.next(this.streams.value.concat(streams.content)));
            
        this.searchTermControl.valueChanges
        .pipe(
            debounceTime(500),
            takeUntil(this.cleanupTime)
        ).subscribe(term => this.fetch((Object.assign({}, this.params.value, { term }))));

        this.taxonomyService.getTenantlessCatalogTaxonomy(this.route.parent.parent.snapshot.params.catalogId, { filterEmpty: true })
        .subscribe(categories => {
            const allSorted = categories.concat(categories.flatMap(cat => cat.subcategories)).sort((a, b) => (a.title?.toLowerCase() > b.title?.toLowerCase()) ? 1 : ((b.title?.toLowerCase() > a.title?.toLowerCase()) ? -1 : 0));
            this.categories = allSorted;
        });

        this.categoryControl.valueChanges
        .pipe(
            filter(categoryId => categoryId !== this.categoryId),
            tap(categoryId => this.categoryId = categoryId),
            map(categoryId => categoryId ? [categoryId] : undefined),
            takeUntil(this.cleanupTime)
        ).subscribe(categoryIds => this.fetch(Object.assign({}, this.params.value, { categoryIds, page: 0})));
    }

    fetch(newParams, clear = true): void {
        this.noTouchingPaging = true;
        if (clear) {
            this.streams.next([]);
        }
        this.params.next(newParams);
    }

    nextPage(): void {
        this.fetch(Object.assign({}, this.params.value, { page: this.params.value.page++ }), false);
    }

    sort(sort: Sort): void {
        this.fetch(Object.assign({}, this.params.value, { page: 0, sort: sort.active, sortDirection: sort.direction}));
    }

    delete(row): void {
        this.tenantService.getCatalog(this.params.value.catalogId).pipe(
            mergeMap(catalog => this.dialog.open(ConfirmationDialogComponent, { data: { title: `Remove ${row.title} from Catalog ${catalog.title}?`,  actionLabel: 'Remove' }}).afterClosed()),
            filter(doIt => !!doIt),
            flatMap(() => this.tenantService.deleteStreamFromCatalog(this.params.value.catalogId, row.id)),
            tap(() => this.snackBar.open(`${row.title} has been removed.`, 'OK', { duration: 3000}))
        ).subscribe(() => this.fetch(Object.assign({}, this.params.value, { page: 0})));
    }

    availableTags(): any[] {
        return _.filter(this.streamTags, tag => !_.includes(this.searchTags.value, tag.id));
    }

    add(event: MatChipInputEvent): void {
        if (event.value?.length) {
            const existingMatch = this.availableTags().find(tag => tag.label.toLowerCase() === event.value.toLocaleLowerCase());
            
            if (existingMatch) {
                if (!this.searchTags.value?.find(tagId => tagId === existingMatch.id)) {
                    const val = this.searchTags.value;
                    val.push(existingMatch);
                    this.searchTags.next(val);
                }
            }

            if (event.input) {
                event.input.value = '';
            }

            this.tagCtrl.setValue(null);
        }
    }

    tagSelected(event: MatAutocompleteSelectedEvent): void {
        const val = this.searchTags.value || []; 
        val.push(event.option.value);
        this.searchTags.next(val);
        this.tagInput.nativeElement.value = '';
        this.tagCtrl.setValue(null);
    }

    removeTag(tag): void {
        const val = this.searchTags.value || [];
        val.splice(val.indexOf(tag), 1);
        this.searchTags.next(val);
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}